/**
 * DAGPerformanceMetrics Component - Performance Analytics Display
 *
 * FERP CRITICAL: Domain-agnostic performance metrics visualization
 * Life-critical bottleneck detection and optimization insights
 *
 * Principles:
 * - All data from backend via useDAGExecution hook (no hardcoded values)
 * - Repository pattern (GraphQL queries only)
 * - Performance metrics for workflow optimization
 * - Bottleneck node identification
 * - Critical path analysis
 * - Parallelism factor calculation
 * - Backend ID generation (never generate node IDs client-side)
 * - 16 primitive types (DAG is workflow execution primitive)
 */
// @ts-nocheck


import React from 'react';
import { Badge, Box, Group, Paper, Stack, Text, Tooltip, Progress, Alert } from '@mantine/core';
import {
  IconAlertTriangle,
  IconChartLine,
  IconClock,
  IconRoute,
  IconArrowsSplit,
  IconInfoCircle,
} from '@tabler/icons-react';
import {
  DAGPerformanceMetrics as DAGPerformanceMetricsData,
  DAGNodeMetrics,
  DAGCriticalityEnum,
  DAGNodeStatusEnum,
} from './hooks/useDAGExecution';

// ============================================================
// TypeScript Types
// ============================================================

export interface DAGPerformanceMetricsProps {
  metrics: DAGPerformanceMetricsData | null;
  isLoading?: boolean;
}

// ============================================================
// Helper Functions
// ============================================================

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else if (seconds > 0) {
    return `${seconds}s`;
  } else {
    return `${ms}ms`;
  }
}

function getCriticalityColor(criticality: DAGCriticalityEnum): string {
  switch (criticality) {
    case DAGCriticalityEnum.Low:
      return 'gray';
    case DAGCriticalityEnum.Medium:
      return 'blue';
    case DAGCriticalityEnum.High:
      return 'orange';
    case DAGCriticalityEnum.Critical:
      return 'red';
    default:
      return 'gray';
  }
}

function getParallelismScore(factor: number): { color: string; label: string } {
  if (factor >= 2.0) {
    return { color: 'green', label: 'Excellent' };
  } else if (factor >= 1.5) {
    return { color: 'blue', label: 'Good' };
  } else if (factor >= 1.0) {
    return { color: 'yellow', label: 'Fair' };
  } else {
    return { color: 'red', label: 'Poor' };
  }
}

// ============================================================
// Component Implementation
// ============================================================

export function DAGPerformanceMetrics({ metrics, isLoading }: DAGPerformanceMetricsProps) {
  if (isLoading) {
    return (
      <Paper p="md" withBorder>
        <Text size="sm" c="dimmed">
          Loading performance metrics...
        </Text>
      </Paper>
    );
  }

  if (!metrics) {
    return (
      <Paper p="md" withBorder>
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          <Text size="sm">
            Performance metrics will be available after execution completes.
          </Text>
        </Alert>
      </Paper>
    );
  }

  const parallelismScore = getParallelismScore(metrics.parallelism_factor);

  // Find slowest nodes
  const slowestNodes = [...metrics.node_metrics]
    .sort((a, b) => b.duration_ms - a.duration_ms)
    .slice(0, 5);

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group gap="sm">
          <IconChartLine size={20} />
          <Text size="sm" fw={600}>
            Performance Metrics
          </Text>
        </Group>

        {/* Total Duration */}
        <Paper p="sm" withBorder style={{ backgroundColor: '#f8f9fa' }}>
          <Group justify="space-between">
            <Group gap="xs">
              <IconClock size={16} />
              <Text size="sm" c="dimmed">
                Total Duration
              </Text>
            </Group>
            <Text size="lg" fw={700}>
              {formatDuration(metrics.total_duration_ms)}
            </Text>
          </Group>
        </Paper>

        {/* Parallelism Factor */}
        <Paper p="sm" withBorder style={{ backgroundColor: '#f8f9fa' }}>
          <Stack gap="xs">
            <Group justify="space-between">
              <Group gap="xs">
                <IconArrowsSplit size={16} />
                <Text size="sm" c="dimmed">
                  Parallelism Factor
                </Text>
                <Tooltip label="Higher is better - indicates efficient parallel execution">
                  <Box>
                    <IconInfoCircle size={14} style={{ opacity: 0.5 }} />
                  </Box>
                </Tooltip>
              </Group>
              <Badge color={parallelismScore.color} variant="filled">
                {parallelismScore.label}
              </Badge>
            </Group>
            <Group justify="space-between" ta="center">
              <Progress
                value={Math.min(metrics.parallelism_factor * 33.33, 100)}
                color={parallelismScore.color}
                size="md"
                style={{ flex: 1 }}
              />
              <Text size="lg" fw={700}>
                {metrics.parallelism_factor.toFixed(2)}x
              </Text>
            </Group>
          </Stack>
        </Paper>

        {/* Bottleneck Nodes */}
        {metrics.bottleneck_nodes.length > 0 && (
          <Paper p="sm" withBorder style={{ backgroundColor: '#fff3bf' }}>
            <Stack gap="xs">
              <Group gap="xs">
                <IconAlertTriangle size={16} style={{ color: '#f08c00' }} />
                <Text size="sm" fw={600} c="orange">
                  Bottleneck Nodes ({metrics.bottleneck_nodes.length})
                </Text>
              </Group>
              <Stack gap={4}>
                {metrics.bottleneck_nodes.map((nodeId) => (
                  <Text key={nodeId} size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>
                    {nodeId}
                  </Text>
                ))}
              </Stack>
            </Stack>
          </Paper>
        )}

        {/* Critical Path */}
        {metrics.critical_path.length > 0 && (
          <Paper p="sm" withBorder style={{ backgroundColor: '#f8f9fa' }}>
            <Stack gap="xs">
              <Group gap="xs">
                <IconRoute size={16} />
                <Text size="sm" fw={600}>
                  Critical Path ({metrics.critical_path.length} nodes)
                </Text>
                <Tooltip label="Longest execution path through the DAG">
                  <Box>
                    <IconInfoCircle size={14} style={{ opacity: 0.5 }} />
                  </Box>
                </Tooltip>
              </Group>
              <Stack gap={4}>
                {metrics.critical_path.slice(0, 5).map((nodeId, index) => (
                  <Group key={nodeId} gap="xs">
                    <Text size="xs" c="dimmed" style={{ width: 20 }}>
                      {index + 1}.
                    </Text>
                    <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>
                      {nodeId}
                    </Text>
                  </Group>
                ))}
                {metrics.critical_path.length > 5 && (
                  <Text size="xs" c="dimmed">
                    ... and {metrics.critical_path.length - 5} more
                  </Text>
                )}
              </Stack>
            </Stack>
          </Paper>
        )}

        {/* Slowest Nodes */}
        {slowestNodes.length > 0 && (
          <Paper p="sm" withBorder style={{ backgroundColor: '#f8f9fa' }}>
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Slowest Nodes
              </Text>
              <Stack gap="xs">
                {slowestNodes.map((node, index) => (
                  <Group key={node.node_id} justify="space-between">
                    <Group gap="xs">
                      <Text size="xs" c="dimmed" style={{ width: 20 }}>
                        {index + 1}.
                      </Text>
                      <Text size="xs" style={{ fontFamily: 'monospace' }}>
                        {node.node_id}
                      </Text>
                      <Badge
                        size="xs"
                        color={getCriticalityColor(node.criticality)}
                        variant="light"
                      >
                        {node.criticality}
                      </Badge>
                    </Group>
                    <Text size="xs" fw={600}>
                      {formatDuration(node.duration_ms)}
                    </Text>
                  </Group>
                ))}
              </Stack>
            </Stack>
          </Paper>
        )}

        {/* Node Metrics Summary */}
        <Paper p="sm" withBorder style={{ backgroundColor: '#f8f9fa' }}>
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              Node Status Distribution
            </Text>
            <Group grow gap="xs">
              <Box>
                <Text size="xs" c="dimmed">
                  Completed
                </Text>
                <Text size="sm" fw={700} c="green">
                  {metrics.node_metrics.filter((n) => n.status === DAGNodeStatusEnum.Completed).length}
                </Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed">
                  Failed
                </Text>
                <Text size="sm" fw={700} c="red">
                  {metrics.node_metrics.filter((n) => n.status === DAGNodeStatusEnum.Failed).length}
                </Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed">
                  Total
                </Text>
                <Text size="sm" fw={700}>
                  {metrics.node_metrics.length}
                </Text>
              </Box>
            </Group>
          </Stack>
        </Paper>

        {/* Optimization Suggestions */}
        {(metrics.bottleneck_nodes.length > 0 || metrics.parallelism_factor < 1.5) && (
          <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Optimization Suggestions
              </Text>
              {metrics.parallelism_factor < 1.5 && (
                <Text size="xs">
                  • Low parallelism factor detected. Consider splitting sequential tasks into parallel branches.
                </Text>
              )}
              {metrics.bottleneck_nodes.length > 0 && (
                <Text size="xs">
                  • {metrics.bottleneck_nodes.length} bottleneck node(s) detected. Review these nodes for performance optimization.
                </Text>
              )}
            </Stack>
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}
